import React, { useState, useEffect } from 'react';
import axios from 'axios';
import mockupConfig from './mockupConfig.json';
import formConfig from './formConfig.json';

let apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

if(!apiBaseUrl) apiBaseUrl = 'https://kitkooapi.herokuapp.com';

const apiEndpoints = {
  generator: {
    images: {
      usingBaseImages: `${apiBaseUrl}/generator/images/using-base-images`
    }
  },
  generatedImage: `${apiBaseUrl}/generated-images/:id`,
  overlayImages: `${apiBaseUrl}/overlay-images`,
  imageInfo: 'https://api.product-generator.kitkoo.com/api/tenant/generator/image-info',
};

const getHeaders = (contentType = 'application/json') => ({
  'Content-Type': contentType,
});

const getClusterConfig = (baseImageId, overlayImageUrl, largeInputValue, colorValue) => {
  let config = mockupConfig[baseImageId];
   if (config) {
    config = config.defaultCluster.map(({ textOptions, position, placementImage: defaultPlacementImage }) => {
      let placementImage;

      if (overlayImageUrl && !textOptions) {
        placementImage = overlayImageUrl;
      } else if (defaultPlacementImage && !textOptions) {
        // Use the default placementImage if it exists and there are no text options
        placementImage = defaultPlacementImage;
      } else {
        // Fallback to text placementImage creation if textOptions exist
        placementImage = {
          text: {
            label: largeInputValue,
            color: colorValue,
            font: textOptions ? textOptions.font : undefined,
            height: textOptions ? textOptions.height : undefined,
            width: textOptions ? textOptions.width : undefined,
          }
        };
      }

      return { position, placementImage };
    });

    return config;
  }

  return null;
}

let cachedOverlayImageId = null;

const uploadNewImage = async (imageInput) => {
  if (cachedOverlayImageId) {
    return { id: cachedOverlayImageId, uri: null };
  }

  const headers = getHeaders();
  let config = { headers };

  let payload;
  if (imageInput instanceof File) {
    let formData = new FormData();
    formData.append('source', imageInput, imageInput.name);
    formData.append('name', 'multiple positions tank top');
    config.headers['Content-Type'] = 'multipart/form-data';
    payload = formData;
  } else {
    payload = {
      name: 'multiple positions tank top',
      sourceUrl: imageInput,
    };
    config.headers['Content-Type'] = 'application/json';
  }

  try {
    const response = await axios.post(apiEndpoints.overlayImages, payload, config);
    cachedOverlayImageId = response.data.data.id;
    console.log('Uploaded image ID:', cachedOverlayImageId); // Add this line
    return { id: cachedOverlayImageId, uri: response.data.data.uri };
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

// Modify the useDevTools hook
const useDevTools = () => {
  const [showDevTools, setShowDevTools] = useState(false);
  const [baseUrl, setBaseUrl] = useState("http://188.245.168.226:8080/api/v2.0.1");
  const [useRenderServer, setUseRenderServer] = useState(false);
  const [clearCache, setClearCache] = useState(true);
  const [fresh, setFresh] = useState(true);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [totalTime, setTotalTime] = useState(null);
  const [delayTime, setDelayTime] = useState(7000);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const devParam = searchParams.get('dev');
    if (devParam === 'dev') {
      setShowDevTools(true);
      console.log("Dev mode activated");
    }
  }, []);

  return { 
    showDevTools, 
    baseUrl, 
    setBaseUrl, 
    useRenderServer, 
    setUseRenderServer,
    clearCache,
    setClearCache,
    fresh,
    setFresh,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    totalTime,
    setTotalTime,
    delayTime,
    setDelayTime
  };
};

const callApi = async (baseImageId, overlayImageId, renderConfig, largeInputValue = '', colorValue, outputSizeEnabled = true, clientId = 'default', devTools) => {
  const headers = getHeaders();
  if (!renderConfig) renderConfig = {};

  const clusterConfig = getClusterConfig(baseImageId, null, largeInputValue, colorValue);

  // Get the client-specific configuration
  const clientConfig = formConfig[clientId] || formConfig.default;

  const baseRenderConfig = {
    ...(largeInputValue && largeInputValue.trim() !== '' 
      ? { cluster: clusterConfig } 
      : { 
          removeBG: clientConfig.removeBG,
          ...('contour' in clientConfig && { contour: clientConfig.contour }),
          clearCache: devTools.showDevTools ? devTools.clearCache : false,
          fresh: devTools.showDevTools ? devTools.fresh : false
        }),
    ...(outputSizeEnabled ? { outputSizeEnabled: true, outputSizeWidth: 300 } : {}),
    ...renderConfig, // Spread renderConfig last to preserve any custom data like palette
  };

  let payload = {
    base_image_uids: [baseImageId],
    render_config: baseRenderConfig,
    overlay_image_id: overlayImageId,
  };

  // Use devTools values here
  if (devTools.useRenderServer && devTools.baseUrl.trim() !== '') {
    payload.render_server = {
      baseUrl: devTools.baseUrl.trim()
    };
  }

  console.log('API Payload:', payload);
  console.log('API Payload JSON:', JSON.stringify(payload, null, 2));

  try {
    const response = await axios.post(apiEndpoints.generator.images.usingBaseImages, payload, { headers });

    if (response.data && response.data.data && response.data.data.length > 0) {
      return { generatedImageId: response.data.data[0].id };
    } else {
      throw new Error('No generated image URL found in the response');
    }
  } catch (error) {
    console.error('Error calling API:', error);
    throw error;
  }
};

const callApiBulk = async (baseImageIds, overlayImageId, renderConfig, largeInputValue, colorValue, outputSizeEnabled, clientId, devTools) => {
  console.log('=== ENTERING callApiBulk function ===');
  console.log('callApiBulk parameters received:', {
    baseImageIds: baseImageIds?.slice(0, 3), // Show first 3 IDs
    overlayImageId,
    renderConfig,
    largeInputValue,
    colorValue,
    outputSizeEnabled,
    clientId
  });

  const headers = getHeaders();
  if (!renderConfig) renderConfig = {};

  console.log('callApiBulk received renderConfig:', JSON.stringify(renderConfig, null, 2));

  const clientConfig = formConfig[clientId] || formConfig.default;

  const finalRenderConfig = {
    removeBG: clientConfig.removeBG,
    ...('contour' in clientConfig && { contour: clientConfig.contour }),
    clearCache: devTools.showDevTools ? devTools.clearCache : false,
    fresh: devTools.showDevTools ? devTools.fresh : false,
    ...(outputSizeEnabled ? { outputSizeEnabled: true, outputSizeWidth: 300 } : {}),
    ...(largeInputValue && largeInputValue.trim() !== '' ? { 
      cluster: baseImageIds.map(id => ({
        ...getClusterConfig(id, null, largeInputValue, colorValue)[0]
      }))
    } : {}),
    ...renderConfig, // Spread renderConfig last to preserve palette data
  };

  console.log('Final render_config before API call:', JSON.stringify(finalRenderConfig, null, 2));

  const payload = {
    base_image_uids: baseImageIds,
    render_config: finalRenderConfig,
    overlay_image_id: overlayImageId,
  };

  if (devTools.useRenderServer && devTools.baseUrl.trim() !== '') {
    payload.render_server = {
      baseUrl: devTools.baseUrl.trim()
    };
  }

  console.log('API Payload:', payload);
  console.log('API Payload JSON:', JSON.stringify(payload, null, 2));

  try {
    const response = await axios.post(apiEndpoints.generator.images.usingBaseImages, payload, { headers });

    if (response.data && response.data.data && response.data.data.length > 0) {
      return response.data.data.map(item => ({
        baseImageId: item.base_image_id,
        generatedImageId: item.id
      }));
    } else {
      throw new Error('No generated image URLs found in the response');
    }
  } catch (error) {
    console.error('Error calling API:', error);
    throw error;
  }
};

const createRateLimiter = (maxRequestsPerSecond) => {
  const queue = [];
  let lastCallTime = 0;

  const processQueue = () => {
    const now = Date.now();
    if (queue.length === 0) return;

    if (now - lastCallTime >= 1000 / maxRequestsPerSecond) {
      const { resolve, reject, fn } = queue.shift();
      lastCallTime = now;
      fn().then(resolve).catch(reject);
    }

    setTimeout(processQueue, Math.max(0, (1000 / maxRequestsPerSecond) - (now - lastCallTime)));
  };

  return (fn) => new Promise((resolve, reject) => {
    queue.push({ resolve, reject, fn });
    if (queue.length === 1) processQueue();
  });
};

const rateLimitedSecondApiCall = createRateLimiter(2);

const callSecondApi = async (id) => {
  console.log('callSecondApi id:', id);
  try {
    const headers = getHeaders();
    let url = apiEndpoints.generatedImage.replace(':id', id);
    const response = await axios.get(url, { headers });
    console.log('callSecondApi response:', response);
    if (!response.data) {
      throw new Error('No data found in the response');
    }
    return response;
  } catch (error) {
    console.error('Error calling second API:', error);
    return null;
  }
};

// Add this rate limiter class
class RateLimiter {
  constructor(maxCallsPerInterval, intervalMs) {
    this.maxCallsPerInterval = maxCallsPerInterval;
    this.intervalMs = intervalMs;
    this.queue = [];
    this.currentCalls = 0;
    this.interval = setInterval(() => this.dispatchCalls(), this.intervalMs);
  }

  dispatchCalls() {
    if (this.queue.length === 0) return;

    while (this.currentCalls < this.maxCallsPerInterval && this.queue.length > 0) {
      const { fn, resolve, reject } = this.queue.shift();
      this.executeFn(fn, resolve, reject);
    }
  }

  executeFn(fn, resolve, reject) {
    this.currentCalls++;
    fn()
      .then(resolve)
      .catch(reject)
      .finally(() => {
        this.currentCalls--;
      });
  }

  schedule(fn) {
    return new Promise((resolve, reject) => {
      this.queue.push({ fn, resolve, reject });
    });
  }

  stop() {
    clearInterval(this.interval);
  }
}

// Create an instance of RateLimiter with a limit of 5 calls per second
const apiRateLimiter = new RateLimiter(5, 1000);

// Add these rate-limited functions
const callApiWithRateLimiting = (...args) => {
  return apiRateLimiter.schedule(() => callApi(...args));
};

const callSecondApiWithRateLimiting = (...args) => {
  return apiRateLimiter.schedule(() => callSecondApi(...args));
};

// Add this new function to fetch multiple generated images
const fetchMultipleGeneratedImages = async (imageIds) => {
  const headers = getHeaders();
  headers['Authorization'] = 'Bearer 15|cyUhrtyS0j8ZTthDCGtcy3dkMsEbQXCLQxMN9tJxVShpbmgM4jGer1Xf7nlVmhPPW5q160O7LiXaIzVMdmcLZrbeXrfO3odulEUZ98UkkWKIruBzaaXOTNPIUC6wHFbhWxjb3BrpqAnlXR86nUmVgKpw1amrt7KmViVoirwiKawAiBpkXCseNOtD4xRFx8TD45LR1KxhAHw0Bx8A0k5C1FYdzXalSAcHJRBTFqE6nIm1y'; // Replace with your actual auth token

  const payload = {
    limit: imageIds.length,
    page: 1,
    aggregations: {
      count: false,
      countThisMonth: false
    },
    filters: {
      id: imageIds
    }
  };

  try {
    const response = await axios.post(
      'https://api.product-generator.kitkoo.com/api/tenant/generated-images/list',
      payload,
      { headers }
    );

    return response.data.data;
  } catch (error) {
    console.error('Error fetching multiple generated images:', error);
    throw error;
  }
};

// Modify the generateMockup function to handle multiple images
const generateMockups = async (baseImageIds, imageId, overlayImageUrl, overlayImageFile, largeInputValue, colorValue, onGeneratedImages, renderConfig = null, outputSizeEnabled, clientId = 'default', devTools) => {
  console.log('=== ENTERING generateMockups function ===');
  console.log('generateMockups ALL parameters:', {
    baseImageIds: baseImageIds?.slice(0, 3),
    imageId,
    overlayImageUrl,
    overlayImageFile,
    largeInputValue,
    colorValue,
    renderConfig,
    outputSizeEnabled,
    clientId
  });
  console.log('Starting generateMockups call with:', { baseImageIds, imageId, overlayImageUrl, renderConfig, clientId });
  console.log('RenderConfig received in generateMockups:', JSON.stringify(renderConfig, null, 2));
  
  const startTime = performance.now();
  if (devTools.showDevTools) {
    devTools.setStartTime(new Date());
    devTools.setEndTime(null);
    devTools.setTotalTime(null);
  }

  try {
    let overlayImageId = imageId;
    if (!overlayImageId) {
      console.log('No overlayImageId provided, uploading new image');
      const uploadResult = await uploadNewImage(overlayImageUrl || overlayImageFile);
      overlayImageId = uploadResult.id;
      console.log('New overlayImageId:', overlayImageId);
    }

    // Track how many low-quality images we're expecting
    const totalExpectedImages = baseImageIds.length;
    let receivedLowQualityImages = 0;
    let receivedHighQualityImages = 0;
    let isLowQualityComplete = false;
    let highQualityResponse = null;
    let lowQualityResponse = null; // Declare this variable outside the try block

    // First API call for low-quality images
    console.log('Calling API for low-quality images');
    console.log('About to call callApiBulk with renderConfig:', JSON.stringify(renderConfig, null, 2));
    console.log('callApiBulk parameters:', {
      baseImageIds,
      overlayImageId,
      renderConfig,
      largeInputValue: largeInputValue || '',
      colorValue,
      outputSizeEnabled: true,
      clientId,
      devTools
    });

    try {
      lowQualityResponse = await callApiBulk(
        baseImageIds,
        overlayImageId,
        renderConfig,
        largeInputValue || '',
        colorValue,
        true,
        clientId,
        devTools
      );
      console.log('API call result for low-quality images:', lowQualityResponse);
    } catch (error) {
      console.error('Error in callApiBulk for low-quality images:', error);
      throw error;
    }

    // Set up polling for images
    let attempts = 0;
    const maxAttempts = 200;
    const pollInterval = 500;
    const lowQualityReadyImages = new Set();
    const highQualityReadyImages = new Set();

    const pollGeneratedImages = async () => {
      if (attempts >= maxAttempts) {
        console.error("Reached the maximum number of attempts");
        onGeneratedImages([], 'failed');
        return;
      }

      attempts++;
      console.log(`Polling attempt ${attempts}`);
      console.log(`Low quality images: ${receivedLowQualityImages}/${totalExpectedImages}`);
      console.log(`High quality images: ${receivedHighQualityImages}/${totalExpectedImages}`);

      try {
        // Poll for low-quality images
        const lowQualityProcessing = lowQualityResponse.filter(item => !lowQualityReadyImages.has(item.generatedImageId));
        if (lowQualityProcessing.length > 0) {
          const lowQualityStatus = await fetchMultipleGeneratedImages(lowQualityProcessing.map(item => item.generatedImageId));
          
          const lowQualityReady = [];
          lowQualityStatus.forEach(image => {
            const item = lowQualityResponse.find(item => item.generatedImageId === image.id);
            if (image.state === "ready") {
              lowQualityReady.push({ 
                id: item.baseImageId, 
                uri: image.uri, 
                state: image.state,
                isHighQuality: false
              });
              lowQualityReadyImages.add(image.id);
              receivedLowQualityImages++;
            }
          });

          if (lowQualityReady.length > 0) {
            console.log(`Sending ${lowQualityReady.length} low quality images to UI`);
            onGeneratedImages(lowQualityReady, 'ready');
          }

          // Check if low quality phase is complete and high quality hasn't started
          if (receivedLowQualityImages >= totalExpectedImages && !isLowQualityComplete) {
            isLowQualityComplete = true;
            const endTime = performance.now();
            const totalTime = (endTime - startTime) / 1000;
            console.log(`All low-quality images received. Total time: ${totalTime} seconds`);
            if (devTools.showDevTools) {
              devTools.setEndTime(new Date());
              devTools.setTotalTime(totalTime);
            }

            // Start high quality generation after delay
            console.log(`Waiting ${devTools.delayTime}ms before calling high-quality API...`);
            await new Promise(resolve => setTimeout(resolve, devTools.delayTime));

            console.log('Calling API for high-quality images');
            console.log('About to call callApiBulk for HIGH QUALITY with renderConfig:', JSON.stringify(renderConfig, null, 2));
            
            try {
              highQualityResponse = await callApiBulk(
                baseImageIds,
                overlayImageId,
                renderConfig,
                largeInputValue || '',
                colorValue,
                false,
                clientId,
                devTools
              );
              console.log('API call result for high-quality images:', highQualityResponse);
            } catch (error) {
              console.error('Error in callApiBulk for high-quality images:', error);
              throw error;
            }
          }
        }

        // Poll for high-quality images if available
        if (highQualityResponse) {
          const highQualityProcessing = highQualityResponse.filter(item => !highQualityReadyImages.has(item.generatedImageId));
          if (highQualityProcessing.length > 0) {
            const highQualityStatus = await fetchMultipleGeneratedImages(highQualityProcessing.map(item => item.generatedImageId));
            
            const highQualityReady = [];
            highQualityStatus.forEach(image => {
              const item = highQualityResponse.find(item => item.generatedImageId === image.id);
              if (image.state === "ready") {
                highQualityReady.push({ 
                  id: item.baseImageId, 
                  uri: image.uri, 
                  state: image.state,
                  isHighQuality: true
                });
                highQualityReadyImages.add(image.id);
                receivedHighQualityImages++;
              }
            });

            if (highQualityReady.length > 0) {
              console.log(`Sending ${highQualityReady.length} high quality images to UI`);
              onGeneratedImages(highQualityReady, 'ready');
            }
          }
        }

        // Continue polling if either quality level is not complete
        const shouldContinuePolling = receivedLowQualityImages < totalExpectedImages || 
          (highQualityResponse && receivedHighQualityImages < totalExpectedImages);

        if (shouldContinuePolling) {
          setTimeout(pollGeneratedImages, pollInterval);
        } else if (receivedLowQualityImages >= totalExpectedImages && receivedHighQualityImages >= totalExpectedImages) {
          console.log('All images are complete');
        }
      } catch (error) {
        console.error(`Error in polling:`, error);
        onGeneratedImages([], 'failed');
      }
    };

    // Start polling immediately after first API call
    pollGeneratedImages();

  } catch (error) {
    console.error('Error in generateMockups:', error);
    onGeneratedImages([], 'failed');
    if (devTools.showDevTools) {
      const endTime = performance.now();
      const totalTime = (endTime - startTime) / 1000;
      devTools.setEndTime(new Date());
      devTools.setTotalTime(totalTime);
    }
  }
};

// Modify the setEndTimeAndTotalTime function
const setEndTimeAndTotalTime = (devTools, startTime) => {
  if (devTools.showDevTools) {
    const endTime = new Date();
    devTools.setEndTime(endTime);
    if (startTime) {
      const totalTime = (endTime.getTime() - startTime.getTime()) / 1000; // in seconds
      devTools.setTotalTime(totalTime);
    } else {
      console.warn('Start time was not provided. Unable to calculate total time.');
      devTools.setTotalTime(null);
    }
  }
};

const resetOverlayImageCache = () => {
  cachedOverlayImageId = null;
};

// Modify the DevTools component
const DevTools = ({ devTools }) => {
  if (!devTools.showDevTools) {
    console.log("DevTools not shown");
    return null;
  }

  const formatDate = (date) => {
    return date ? date.toLocaleString() : 'N/A';
  };

  const formatDuration = (seconds) => {
    if (seconds === null) return 'N/A';
    seconds = Math.round(seconds); // Round to nearest second
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    let result = '';
    if (hours > 0) {
      result += `${hours}h `;
    }
    if (minutes > 0 || hours > 0) {
      result += `${minutes}m `;
    }
    result += `${remainingSeconds}s`;
    return result.trim();
  };

  console.log("Rendering DevTools");
  return (
    <div className="dev-tools" style={{ padding: '20px', background: '#f0f0f0', marginBottom: '20px' }}>
      <h3>Dev Tools</h3>
      <div>
        <label>
          <input
            type="checkbox"
            checked={devTools.useRenderServer}
            onChange={(e) => devTools.setUseRenderServer(e.target.checked)}
          />
          Use Render Server
        </label>
      </div>
      <div>
        <label>
          Base URL:
          <input
            type="text"
            value={devTools.baseUrl}
            onChange={(e) => devTools.setBaseUrl(e.target.value)}
            disabled={!devTools.useRenderServer}
            style={{ width: '100%', marginTop: '5px' }}
          />
        </label>
      </div>
      <div>
        <label>
          <input
            type="checkbox"
            checked={devTools.clearCache}
            onChange={(e) => devTools.setClearCache(e.target.checked)}
          />
          Clear Cache
        </label>
      </div>
      <div>
        <label>
          <input
            type="checkbox"
            checked={devTools.fresh}
            onChange={(e) => devTools.setFresh(e.target.checked)}
          />
          Fresh
        </label>
      </div>
      <div style={{ marginTop: '10px' }}>
        <label>
          Delay between calls (ms):
          <input
            type="number"
            value={devTools.delayTime}
            onChange={(e) => devTools.setDelayTime(Number(e.target.value))}
            min="0"
            step="500"
            style={{ width: '100%', marginTop: '5px' }}
          />
        </label>
      </div>
      <div>
        <h4>Timing Information</h4>
        <p>Start Time: {formatDate(devTools.startTime)}</p>
        <p>End Time: {formatDate(devTools.endTime)}</p>
        <p>Total Time: {formatDuration(devTools.totalTime)}</p>
      </div>
    </div>
  );
};

// Add new function to extract color palette
const extractColorPalette = async (overlayImageId) => {
  try {
    const headers = getHeaders();
    headers['Authorization'] = 'Bearer 15|cyUhrtyS0j8ZTthDCGtcy3dkMsEbQXCLQxMN9tJxVShpbmgM4jGer1Xf7nlVmhPPW5q160O7LiXaIzVMdmcLZrbeXrfO3odulEUZ98UkkWKIruBzaaXOTNPIUC6wHFbhWxjb3BrpqAnlXR86nUmVgKpw1amrt7KmViVoirwiKawAiBpkXCseNOtD4xRFx8TD45LR1KxhAHw0Bx8A0k5C1FYdzXalSAcHJRBTFqE6nIm1y';

    const response = await axios.post(apiEndpoints.imageInfo, {
      render_config: {
        actions: [
          {
            type: "CollectPalette",
            params: {
              colorCount: 10,
              quality: 4
            }
          }
        ]
      },
      overlay_image_ids: [overlayImageId]
    }, { headers });

    if (response.data && response.data.data && response.data.data.length > 0) {
      const paletteData = response.data.data[0];
      if (paletteData.status === 'success' && paletteData.info && paletteData.info.palette) {
        return paletteData.info.palette.images.placement.slice(0, 10); // Return top 10 colors
      }
    }
    
    throw new Error('No palette data found in response');
  } catch (error) {
    console.error('Error extracting color palette:', error);
    throw error;
  }
};

export { uploadNewImage, callApi, callSecondApi, generateMockups, resetOverlayImageCache, callApiWithRateLimiting, callSecondApiWithRateLimiting, DevTools, useDevTools, fetchMultipleGeneratedImages, callApiBulk, extractColorPalette };